import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { detectUserCurrency, getCurrency, Currency } from '../utils/currency';

interface CurrencyContextType {
  currency: string;
  currencyInfo: Currency | undefined;
  setCurrency: (currency: string) => void;
  isLoading: boolean;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrencyState] = useState<string>('EUR');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Try to load from localStorage first
    const savedCurrency = localStorage.getItem('preferredCurrency');
    if (savedCurrency) {
      setCurrencyState(savedCurrency);
      setIsLoading(false);
      return;
    }

    // Otherwise, detect from geolocation
    detectUserCurrency().then((detectedCurrency) => {
      setCurrencyState(detectedCurrency);
      setIsLoading(false);
    }).catch(() => {
      setCurrencyState('EUR');
      setIsLoading(false);
    });
  }, []);

  const setCurrency = (newCurrency: string) => {
    setCurrencyState(newCurrency);
    localStorage.setItem('preferredCurrency', newCurrency);
  };

  const currencyInfo = getCurrency(currency);

  return (
    <CurrencyContext.Provider value={{ currency, currencyInfo, setCurrency, isLoading }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}

